import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  Paper,
  Grid,
  Card,
  CardContent,
  CircularProgress,
  Button,
  Stack,
  Alert,
  Chip,
} from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  ArcElement,
  PointElement,
} from 'chart.js';
import { Pie, Line } from 'react-chartjs-2';
import { format, subDays } from 'date-fns';
import activityService from '../../services/activityService';
import RefreshIcon from '@mui/icons-material/Refresh';
import EmptyState from '../ui/EmptyState';

ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  LineElement,
  PointElement,
  ArcElement,
  Title,
  Tooltip,
  Legend
);

interface ActivityStatsProps {
  userId?: string;
  height?: string | number;
}

type NormalizedStats = {
  total: number;
  byType: Array<{ type: string; count: number }>;
  byDay: Array<{ date: string; count: number }>;
};

const normalizeStats = (raw: any): NormalizedStats | null => {
  if (!raw) return null;

  const byTypeRaw = raw.byType;
  const byDayRaw = raw.byDay;

  const byType = Array.isArray(byTypeRaw)
    ? byTypeRaw
    : byTypeRaw && typeof byTypeRaw === 'object'
      ? Object.entries(byTypeRaw).map(([type, count]) => ({ type, count: Number(count) || 0 }))
      : [];

  const byDay = Array.isArray(byDayRaw)
    ? byDayRaw
    : byDayRaw && typeof byDayRaw === 'object'
      ? Object.entries(byDayRaw).map(([date, count]) => ({ date, count: Number(count) || 0 }))
      : [];

  return {
    total: Number(raw.total ?? raw.totalActivities ?? 0) || 0,
    byType,
    byDay: byDay.sort((a, b) => String(a.date).localeCompare(String(b.date))),
  };
};

const ActivityStats: React.FC<ActivityStatsProps> = ({
  userId,
  height = 'auto',
}) => {
  const [stats, setStats] = useState<NormalizedStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [filters, setFilters] = useState({
    startDate: subDays(new Date(), 30) as Date | null,
    endDate: new Date() as Date | null,
  });

  const fetchActivityStats = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      const startDateStr = filters.startDate
        ? format(filters.startDate, 'yyyy-MM-dd')
        : undefined;
      const endDateStr = filters.endDate
        ? format(filters.endDate, 'yyyy-MM-dd')
        : undefined;

      const statsData = await activityService.getActivityStats(
        userId,
        startDateStr,
        endDateStr
      );

      setStats(normalizeStats(statsData));
    } catch {
      setError('Failed to load activity statistics. Please try again.');
      setStats(null);
    } finally {
      setLoading(false);
    }
  }, [userId, filters.startDate, filters.endDate]);

  useEffect(() => {
    fetchActivityStats();
  }, [fetchActivityStats]);

  const handleDateChange = (field: 'startDate' | 'endDate') => (date: Date | null) => {
    setFilters((prev) => ({ ...prev, [field]: date }));
  };

  const formatActivityType = (type: string): string =>
    type
      .split('_')
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
      .join(' ');

  const getActivityTypeData = () => {
    if (!stats?.byType?.length) return null;
    return {
      labels: stats.byType.map((item) => formatActivityType(item.type)),
      datasets: [
        {
          label: 'Activities by type',
          data: stats.byType.map((item) => item.count),
          backgroundColor: [
            'rgba(63, 81, 181, 0.7)',
            'rgba(0, 150, 136, 0.7)',
            'rgba(255, 152, 0, 0.7)',
            'rgba(244, 67, 54, 0.7)',
            'rgba(156, 39, 176, 0.7)',
            'rgba(33, 150, 243, 0.7)',
            'rgba(121, 85, 72, 0.7)',
          ],
          borderWidth: 0,
        },
      ],
    };
  };

  const getActivityByDayData = () => {
    if (!stats?.byDay?.length) return null;
    return {
      labels: stats.byDay.map((item) => {
        try {
          return format(new Date(item.date), 'MMM d');
        } catch {
          return item.date;
        }
      }),
      datasets: [
        {
          label: 'Activities',
          data: stats.byDay.map((item) => item.count),
          backgroundColor: 'rgba(63, 81, 181, 0.15)',
          borderColor: 'rgba(63, 81, 181, 1)',
          borderWidth: 2,
          tension: 0.35,
          fill: true,
        },
      ],
    };
  };

  const lineOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: { display: false },
    },
    scales: {
      y: { beginAtZero: true, ticks: { precision: 0 } },
    },
  };

  const pieOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: { position: 'bottom' as const },
    },
  };

  const typeData = getActivityTypeData();
  const dayData = getActivityByDayData();

  return (
    <Box sx={{ height, width: '100%', overflowY: 'auto' }}>
      <Stack
        direction={{ xs: 'column', sm: 'row' }}
        justifyContent="space-between"
        alignItems={{ xs: 'stretch', sm: 'center' }}
        spacing={1.5}
        sx={{ mb: 2 }}
      >
        <Box>
          <Typography variant="subtitle1" fontWeight={600}>
            Statistics{userId ? ' for user' : ''}
          </Typography>
          <Typography variant="caption" color="text.secondary">
            Activity totals for the selected range
          </Typography>
        </Box>

        <Stack direction="row" spacing={1.5} alignItems="center" flexWrap="wrap">
          <LocalizationProvider dateAdapter={AdapterDateFns}>
            <DatePicker
              label="From"
              value={filters.startDate}
              onChange={handleDateChange('startDate')}
              slotProps={{ textField: { size: 'small', sx: { minWidth: 140 } } }}
            />
            <DatePicker
              label="To"
              value={filters.endDate}
              onChange={handleDateChange('endDate')}
              slotProps={{ textField: { size: 'small', sx: { minWidth: 140 } } }}
            />
          </LocalizationProvider>
          <Button
            variant="outlined"
            size="small"
            onClick={fetchActivityStats}
            startIcon={<RefreshIcon />}
            sx={{ textTransform: 'none' }}
          >
            Refresh
          </Button>
        </Stack>
      </Stack>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
          <CircularProgress size={32} />
        </Box>
      ) : !stats || (stats.total === 0 && stats.byType.length === 0) ? (
        <EmptyState
          type="default"
          title="No statistics yet"
          description="Activity charts will appear once there are events in this date range."
        />
      ) : (
        <Grid container spacing={2}>
          <Grid item xs={12} md={4}>
            <Card variant="outlined" sx={{ height: '100%' }}>
              <CardContent>
                <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                  Total activities
                </Typography>
                <Typography variant="h4" fontWeight={600}>
                  {stats.total.toLocaleString()}
                </Typography>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12} md={8}>
            <Card variant="outlined" sx={{ height: '100%' }}>
              <CardContent>
                <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                  By type
                </Typography>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75, mt: 0.5 }}>
                  {stats.byType.length === 0 ? (
                    <Typography variant="body2" color="text.secondary">
                      No type breakdown
                    </Typography>
                  ) : (
                    stats.byType.map((type) => (
                      <Chip
                        key={type.type}
                        label={`${formatActivityType(type.type)} (${type.count})`}
                        size="small"
                        variant="outlined"
                      />
                    ))
                  )}
                </Box>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12} md={6}>
            <Paper
              variant="outlined"
              sx={{ p: 2, height: 320, display: 'flex', flexDirection: 'column' }}
            >
              <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                Activities by type
              </Typography>
              <Box sx={{ flex: 1, minHeight: 0 }}>
                {typeData ? (
                  <Pie data={typeData} options={pieOptions} />
                ) : (
                  <Box sx={{ height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <Typography variant="body2" color="text.secondary">
                      No type data
                    </Typography>
                  </Box>
                )}
              </Box>
            </Paper>
          </Grid>

          <Grid item xs={12} md={6}>
            <Paper
              variant="outlined"
              sx={{ p: 2, height: 320, display: 'flex', flexDirection: 'column' }}
            >
              <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                Activity over time
              </Typography>
              <Box sx={{ flex: 1, minHeight: 0 }}>
                {dayData ? (
                  <Line options={lineOptions} data={dayData} />
                ) : (
                  <Box sx={{ height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <Typography variant="body2" color="text.secondary">
                      No timeline data
                    </Typography>
                  </Box>
                )}
              </Box>
            </Paper>
          </Grid>
        </Grid>
      )}
    </Box>
  );
};

export default ActivityStats;
